import { useCallback, useEffect, useMemo, useState, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { Search } from '@shared/components/ui/Search';
import { FiltersDropdown } from '@shared/components/ui/FiltersDropdown';
import { ColumnSelector } from '@shared/components/ui/ColumnSelector';
import { ColumnDef } from '@tanstack/react-table';
import { BankAccount } from '../../models/BankAccount';
import { cn } from '@shared/utils/utils';
import { BankAccountsFiltersPanel } from './BankAccountsFiltersPanel';

interface FiltersSectionProps {
  columns: ColumnDef<BankAccount>[];
  selectedColumns: string[];
  fixedColumns?: string[];
  onColumnsChange: (selected: string[]) => void;
  searchValue: string;
  onSearch: (value: string) => void;
  filterConditions: FilterConditionOutput[];
  onFilterChange: (conditions: FilterConditionOutput[]) => void;
  teamIds?: string[];
  onTeamIdsChange?: (teamIds: string[]) => void;
  className?: string;
}

export const FiltersSection = ({
  columns,
  selectedColumns,
  fixedColumns = [],
  onColumnsChange,
  searchValue,
  onSearch,
  filterConditions,
  onFilterChange,
  teamIds = [],
  onTeamIdsChange,
  className,
}: FiltersSectionProps) => {
  const { t } = useTranslation('bankAccountsModule');
  const { t: tUI } = useTranslation('ui');

  const [inputValue, setInputValue] = useState(searchValue);

  useEffect(() => {
    setInputValue((prev) => (prev === searchValue ? prev : searchValue));
  }, [searchValue]);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const handleSearchSubmit = useCallback(() => {
    onSearch(inputValue.trim());
  }, [inputValue, onSearch]);

  const activeFiltersCount = useMemo(() => {
    let count = filterConditions.length;
    if (teamIds.length > 0) count++;
    return count;
  }, [filterConditions, teamIds.length]);

  return (
    <div className={cn('flex flex-wrap items-center gap-3', className)}>
      {/* Buscador */}
      <Search
        placeholder={t('search.placeholder')}
        value={inputValue}
        onChange={handleInputChange}
        onEnter={handleSearchSubmit}
        dataTestId="bank-accounts-search"
        inputDataTestId="bank-accounts-search-input"
      />

      {/* Filtros avanzados */}
      <FiltersDropdown
        title={tUI('advancedFilter.filters', 'Filtros')}
        activeFiltersCount={activeFiltersCount}
        dropdownContentClassName="sm:min-w-[350px] sm:max-w-[400px] p-4 bg-body-bg text-foreground border border-card-border"
      >
        <BankAccountsFiltersPanel
          initialConditions={filterConditions}
          onChange={onFilterChange}
          teamIds={teamIds}
          onTeamIdsChange={onTeamIdsChange}
        />
      </FiltersDropdown>

      {/* Selector de columnas */}
      <ColumnSelector
        columns={columns}
        selected={selectedColumns}
        fixedColumns={fixedColumns}
        onColumnsChange={onColumnsChange}
        dataTestId="bank-accounts-column-selector"
        storageKey="bank-accounts-ag-grid-table"
      />
    </div>
  );
};
